// src/pages/product/ProductBulkUploadDetail.tsx
//
// ProductBulkUploadDetail
// - Detail page for a product bulk upload (aligned with eSIM bulk detail UX).
//
// Refactor highlights:
// - AppPageHeader slots:
//   - leading: filename + upload type description
//   - actions: inject "Confirm Upload" only when status === PENDING
// - Remove footer actions (navigation is handled by route header/back)
// - Keep Summary card in the body (counts + status + createdAt/updatedAt)
// - Keep "Only non-OK rows" filter (maps to API param onlyFailed)
// - Use StandardTableFrame getRowStatus for non-OK highlighting (row-level)
// - Use StandardTableToolbar + StandardPagination
//
// Notes:
// - Count API does NOT reflect the "Only non-OK" filter; pagination totals may not match filtered rows.
// - Legacy behavior preserved: return null while summary is loading.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { CloudUploadOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';

import { useUploadDetail } from '@queries/productBulk/useUploadDetail';
import { useUploadDetails } from '@queries/productBulk/useUploadDetails';
import { useUploadDetailsCount } from '@queries/productBulk/useUploadDetailsCount';
import { confirmUpload } from '@apis/productBulk/upload';

import { Status, UploadStatus, ProductStatus } from '@models/product';
import { ProductSaleType, ProductType } from '@apis/product';

import {
  PRODUCT_SALE_TYPE_LABELS,
  PRODUCT_STATUS_LABELS,
  PRODUCT_TYPE_LABELS,
  getLabel,
} from '@utils/productLabels';
import { toUsdDisplay } from '@utils/money';
import { timestampToDate } from '@utils/time';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';
import { usePageHeader } from '@components/common/pageHeader.context';

import {
  StandardTableToolbar,
  StandardTableFrame,
  StandardPagination,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;

type UploadDetailSummary = {
  productBulkUploadId: number;
  uploadType: 'CREATE' | 'UPDATE' | string;
  createdAt: number;
  filename: string;
  isDeleted: boolean;
  updatedAt: number;
  okayCount: number;
  failedCount: number;
  totalCount: number;
  status: Status;
};

const DEFAULT_SUMMARY: UploadDetailSummary = {
  productBulkUploadId: 0,
  uploadType: 'CREATE',
  createdAt: 0,
  filename: '',
  isDeleted: false,
  updatedAt: 0,
  okayCount: 0,
  failedCount: 0,
  totalCount: 0,
  status: Status.PENDING,
};

type UploadDetailRow = {
  productBulkUploadDetailId: number | string;
  productCode?: string | null;
  productStatus?: ProductStatus | string | null;
  productSaleType?: ProductSaleType | string | null;
  productType?: ProductType | string | null;
  productName?: string | null;
  price?: number | string | null;
  description?: string | null;
  specification?: unknown;
  requiredCustomerData?: unknown;
  uploadStatus?: UploadStatus | string | null;
};

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

const formatNumber = (n: number | undefined | null) => Number(n ?? 0).toLocaleString();

const dash = (v: unknown) => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.trim() ? v : '-';
  return String(v);
};

const normalizeUploadType = (t?: string | null) => String(t ?? '').trim().toUpperCase();

const uploadTypeLabel = (t?: string | null) => {
  const v = normalizeUploadType(t);
  if (v === 'CREATE' || v === 'CREATED') return 'Create';
  if (v === 'UPDATE') return 'Update';
  return dash(t);
};

const statusChipProps = (
  status?: Status | string | null,
): { label: string; color: 'default' | 'success' | 'primary' | 'warning'; variant: 'filled' | 'outlined' } => {
  const v = String(status ?? '');
  if (v === Status.COMPLETED) return { label: 'Completed', color: 'success', variant: 'filled' };
  if (v === Status.PROCESSING) return { label: 'Processing', color: 'primary', variant: 'outlined' };
  if (v === Status.PENDING) return { label: 'Pending', color: 'warning', variant: 'filled' };
  return { label: dash(status), color: 'default', variant: 'outlined' };
};

const uploadStatusChipProps = (
  status?: UploadStatus | string | null,
): { label: string; color: 'primary' | 'warning' | 'error' | 'default'; variant: 'filled' | 'outlined' } => {
  const v = String(status ?? '');
  if (v === UploadStatus.OK) return { label: 'OK', color: 'primary', variant: 'outlined' };
  if (v === UploadStatus.DUPLICATE) return { label: 'Duplicate', color: 'warning', variant: 'filled' };
  if (v === UploadStatus.ALREADY_EXIST) return { label: 'Already exist', color: 'error', variant: 'filled' };
  return { label: dash(status), color: 'default', variant: 'outlined' };
};

/**
 * Safely pretty-print JSON-like values for table cells.
 * - If `raw` is a JSON string, try parsing and formatting it.
 * - If parsing fails, fall back to the original string.
 */
const formatJsonForCell = (raw: unknown): string => {
  if (raw === null || raw === undefined) return '-';

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return '-';

    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return raw;
    }
  }

  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
};

const ProductBulkUploadDetail = () => {
  const { uploadId } = useParams();
  const navigate = useNavigate();

  const confirm = useConfirm();
  const systemAlert = useSystemAlert();
  const { setPageHeaderSlots } = usePageHeader();

  const safeUploadId = useMemo(() => {
    const n = Number(uploadId);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [uploadId]);

  const [page, setPage] = useState(1);
  const [onlyNonOk, setOnlyNonOk] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const { data: uploadDetail, isLoading: isSummaryLoading } = useUploadDetail(safeUploadId);

  const { data: uploadDetailsData, isLoading: isRowsLoading } = useUploadDetails({
    uploadId: safeUploadId,
    page,
    pageSize: PAGE_SIZE,
    params: { onlyFailed: onlyNonOk },
  });

  const { data: totalCount, isLoading: isCountLoading } = useUploadDetailsCount(safeUploadId);

  const summary: UploadDetailSummary = uploadDetail?.data?.[0] ?? DEFAULT_SUMMARY;
  const rows: UploadDetailRow[] = (uploadDetailsData?.data ?? []) as UploadDetailRow[];

  const totalPages = useMemo(() => {
    const count = totalCount ?? 0;
    return Math.max(1, Math.ceil(count / PAGE_SIZE));
  }, [totalCount]);

  useEffect(() => {
    setPage((prev) => clamp(prev, 1, totalPages));
  }, [totalPages]);

  const handleChangePage = useCallback(
    (next: number) => setPage(clamp(next, 1, totalPages)),
    [totalPages],
  );

  const handleToggleOnlyNonOk = useCallback(() => {
    setOnlyNonOk((prev) => !prev);
    setPage(1);
  }, []);

  const handleConfirmUpload = useCallback(async () => {
    if (!safeUploadId) {
      systemAlert.error('Invalid uploadId.');
      return;
    }
    if (isConfirming) return;

    const ok = await confirm({
      priority: 'warning',
      title: 'Confirm upload?',
      description: 'This will apply the uploaded changes. Continue?',
      confirmText: 'Confirm Upload',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    setIsConfirming(true);
    try {
      const { message } = await confirmUpload(safeUploadId);
      if (message !== 'OK') {
        systemAlert.error(message || 'Failed to confirm upload.');
        return;
      }

      systemAlert.success('Upload confirmed.');
      navigate('/products/upload');
    } catch {
      systemAlert.error('Failed to confirm upload.');
    } finally {
      setIsConfirming(false);
    }
  }, [confirm, isConfirming, navigate, safeUploadId, systemAlert]);

  useEffect(() => {
    const canConfirm = summary.status === Status.PENDING && Boolean(safeUploadId);

    const leadingTitle = summary.filename ? summary.filename : undefined;
    const leadingDesc = summary.uploadType ? `Upload type: ${uploadTypeLabel(summary.uploadType)}` : undefined;

    setPageHeaderSlots({
      leading: { title: leadingTitle, description: leadingDesc },
      actions: canConfirm
        ? [
            {
              key: 'confirm-upload',
              label: isConfirming ? 'Confirming…' : 'Confirm Upload',
              onClick: handleConfirmUpload,
              startIcon: <CloudUploadOutlined />,
              variant: 'contained',
              primary: true,
              disabled: isConfirming,
            },
          ]
        : undefined,
    });

    // Clear on unmount to avoid header leakage across routes.
    return () => setPageHeaderSlots({});
  }, [handleConfirmUpload, isConfirming, safeUploadId, setPageHeaderSlots, summary.filename, summary.status, summary.uploadType]);

  // Legacy behavior: return null while summary is loading.
  if (isSummaryLoading) return null;

  const isLoading = Boolean(isRowsLoading || isCountLoading);
  const columnsCount = 11;

  const statusChip = statusChipProps(summary.status);

  return (
    <Box width="100%">
      <Box mt={2}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                Upload Summary
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overview of file and processing result.
              </Typography>
            </Box>

            <Chip size="small" label={statusChip.label} color={statusChip.color} variant={statusChip.variant} sx={{ mt: 0.5 }} />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="caption" color="text.secondary">
                File
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{
                  ...cellSx.nowrap,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  maxWidth: '100%',
                }}
                title={summary.filename}
              >
                {dash(summary.filename)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Upload Type
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {uploadTypeLabel(summary.uploadType)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="body2" fontWeight={800} sx={cellSx.number}>
                {formatNumber(summary.totalCount)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                OK
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={{ ...cellSx.number, color: 'success.main' }}>
                {formatNumber(summary.okayCount)}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary">
                Failed
              </Typography>
              <Typography
                variant="body2"
                fontWeight={(summary.failedCount ?? 0) > 0 ? 800 : 600}
                sx={{
                  ...cellSx.number,
                  color: (summary.failedCount ?? 0) > 0 ? 'error.main' : 'text.secondary',
                }}
              >
                {formatNumber(summary.failedCount)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Created At
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={cellSx.nowrap}>
                {summary.createdAt ? timestampToDate(summary.createdAt) : '-'}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Updated At
              </Typography>
              <Typography variant="body2" fontWeight={700} sx={cellSx.nowrap}>
                {summary.updatedAt ? timestampToDate(summary.updatedAt) : '-'}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={<Switch checked={onlyNonOk} onChange={handleToggleOnlyNonOk} color="warning" />}
            label="Only non-OK rows"
          />
        </Paper>
      </Box>

      <Box mt={2}>
        <StandardTableToolbar
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount ?? 0}
          onChangePage={handleChangePage}
          showTotalCount
          showPageJump
        />

        <StandardTableFrame<UploadDetailRow>
          head={
            <TableRow>
              <TableCell width={70} align="right" sx={cellSx.nowrap}>
                #
              </TableCell>
              <TableCell sx={cellSx.nowrap}>Product Code</TableCell>
              <TableCell align="center" sx={cellSx.nowrap}>
                Product Status
              </TableCell>
              <TableCell align="center" sx={cellSx.nowrap}>
                Sale Type
              </TableCell>
              <TableCell align="center" sx={cellSx.nowrap}>
                Product Type
              </TableCell>
              <TableCell sx={cellSx.nowrap}>Product Name</TableCell>
              <TableCell align="right" sx={cellSx.nowrap}>
                Price
              </TableCell>
              <TableCell sx={cellSx.nowrap}>Description</TableCell>
              <TableCell sx={cellSx.nowrap}>Specification</TableCell>
              <TableCell sx={cellSx.nowrap}>Customer Data</TableCell>
              <TableCell align="center" sx={cellSx.nowrap}>
                Result
              </TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.productBulkUploadDetailId)}
          columnsCount={columnsCount}
          isLoading={isLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText={onlyNonOk ? 'No non-OK rows found.' : 'No rows found.'}
          minWidth={1600}
          getRowStatus={(row) => (String(row.uploadStatus) !== UploadStatus.OK ? 'error' : 'normal')}
          renderRow={(row, index) => {
            const uploadStatusChip = uploadStatusChipProps(row.uploadStatus);

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell sx={cellSx.nowrap}>{dash(row.productCode)}</TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={getLabel(PRODUCT_STATUS_LABELS, row.productStatus as ProductStatus)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={getLabel(PRODUCT_SALE_TYPE_LABELS, row.productSaleType as ProductSaleType)}
                    size="small"
                    variant={row.productSaleType === ProductSaleType.REAL_TIME ? 'filled' : 'outlined'}
                  />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={getLabel(PRODUCT_TYPE_LABELS, row.productType as ProductType)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>

                <TableCell sx={{ minWidth: 200 }}>{dash(row.productName)}</TableCell>

                <TableCell align="right" sx={cellSx.number}>
                  {toUsdDisplay(Number(row.price ?? 0))}
                </TableCell>

                <TableCell sx={{ minWidth: 260, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {dash(row.description)}
                </TableCell>

                <TableCell sx={{ minWidth: 320 }}>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: 12,
                      lineHeight: 1.4,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    }}
                  >
                    {formatJsonForCell(row.specification)}
                  </Box>
                </TableCell>

                <TableCell sx={{ minWidth: 320 }}>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: 12,
                      lineHeight: 1.4,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    }}
                  >
                    {formatJsonForCell(row.requiredCustomerData)}
                  </Box>
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  <Chip
                    label={uploadStatusChip.label}
                    color={uploadStatusChip.color}
                    variant={uploadStatusChip.variant}
                    size="small"
                  />
                </TableCell>
              </>
            );
          }}
        />

        <Box mt={2} display="flex" justifyContent="center">
          <StandardPagination
            page={page}
            pageSize={PAGE_SIZE}
            totalCount={totalCount ?? 0}
            onChangePage={handleChangePage}
            size="small"
            shape="rounded"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ProductBulkUploadDetail;
